import { FichaService } from './../../../ficha-veiculo.service';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FichaCadastro } from '../../ficha-cadastro';
import { ClasseProfissionalService } from '../../../../services/classe-profissional/classe-profissional.service';
import { take, takeWhile } from 'rxjs/operators';
import { ProfissaoService } from '../../../../services/profissao/profissao.service';
import { onlyDigits } from '../../../../shared/helper/utils';

@Component({
  selector: 'app-dados-conjuge',
  templateUrl: './dados-conjuge.component.html',
  styleUrls: ['./dados-conjuge.component.scss'],
})
export class DadosConjugeComponent implements OnInit, OnDestroy {
  @Input() fichaCadastro: FichaCadastro;
  @Input() cpfCliente: string;

  @Output() conjugeFormValidEmit = new EventEmitter();
  @Output() conjugeFormEmit = new EventEmitter();

  isAlive = true;
  isValidatingCpfConjuge = false;
  conjugeForm: FormGroup;
  classesProfissionais: any = [];
  profissoesConjuge: any[];

  dataRange = {
    min: 15,
    max: 95,
    message: 'A idade deve ser entre 15 e 95 anos',
  };

  public errorMessage: string = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly classeProfissionalService: ClasseProfissionalService,
    private readonly profissaoService: ProfissaoService,
    private readonly fichaService: FichaService
  ) {}

  ngOnInit() {
    this.initForm();
    this.initDefaultObservers();
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  initForm() {
    this.conjugeForm = this.fb.group({
      nomeConjuge: ['', [Validators.required, Validators.maxLength(60)]],
      cpfConjuge: ['', [Validators.required]],
      dataNascimentoConjuge: ['', [Validators.required]],
      telefoneConjuge: this.fb.control('', {
        validators: [Validators.required],
        asyncValidators: [this.validCelphone()],
        updateOn: 'blur',
      }),
      classeProfConjuge: ['', [Validators.required]],
      cargoConjuge: ['', [Validators.required]],
    });

    if (!this.conjugeForm.get('cargoConjuge').value) {
      this.conjugeForm.get('cargoConjuge').disable();
    }
  }

  initDefaultObservers() {
    this.fillClassesProfissionais();

    this.conjugeForm
      .get('classeProfConjuge')
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((res) => this.fillProfissoesConjuge(res));

    this.conjugeForm.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        this.conjugeFormValidEmit.next(this.isValid());
        this.conjugeFormEmit.next(this.conjugeForm.getRawValue());
      });
  }

  isValid(): boolean {
    return this.conjugeForm.valid && this.cpfConjuge.value !== this.cpfCliente;
  }

  private fillClassesProfissionais(): void {
    this.classeProfissionalService
      .classes()
      .pipe(take(1))
      .subscribe((res) => (this.classesProfissionais = res));
  }

  private fillProfissoesConjuge(res: number, clear = true): void {
    this.profissaoService
      .profissoes(res)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((res) => (this.profissoesConjuge = res.profissoes));
  }

  onClasseProfissionalChange() {
    this.conjugeForm.get('cargoConjuge').reset();
    this.conjugeForm.get('cargoConjuge').enable();
  }

  get nomeConjuge(): AbstractControl {
    return this.conjugeForm.get('nomeConjuge');
  }

  get cpfConjuge(): AbstractControl {
    return this.conjugeForm.get('cpfConjuge');
  }

  get dataNascimentoConjuge(): AbstractControl {
    return this.conjugeForm.get('dataNascimentoConjuge').value;
  }

  get telefoneConjuge(): AbstractControl {
    return this.conjugeForm.get('telefoneConjuge').value;
  }

  get classesProfissionaisConjuge() {
    return this.classesProfissionais;
  }

  get descricaoClasseProfissionaisConjuge() {
    const classeProfissionalId = Number(
      this.conjugeForm.get('classeProfConjuge').value
    );
    const classe = this.classesProfissionais.filter(
      (e) => e.id === classeProfissionalId
    )[0] || { descricao: 'nao informado' };
    return classe.descricao;
  }

  get cargoConjuge() {
    return (
      this.profissoesConjuge.find(
        (cargo) =>
          cargo.id === Number(this.conjugeForm.get('cargoConjuge').value)
      ) || {}
    );
  }

  getDddAndCelular(value): { ddd: string; telefone: string } {
    if (!value || value === '') return { ddd: '', telefone: '' };
    const digits = onlyDigits(value);
    const ddd = digits.substring(0, 2);
    const telefone = digits.substring(2);
    return { ddd, telefone };
  }

  validCelphone(): AsyncValidatorFn {
    this.errorMessage = null;
    return (control: AbstractControl): Promise<ValidationErrors | null> => {
      if (!control.value || control.value.length === 0) {
        return Promise.resolve(null);
      }
      const dddCelular = this.getDddAndCelular(control.value);
      return this.fichaService
        .verifyCellPhone(dddCelular.ddd, dddCelular.telefone)
        .pipe(take(1))
        .toPromise()
        .then((res) => {
          if (res.status !== 'FAIL') {
            this.errorMessage = null;
            return null;
          }
          this.errorMessage = res.messages[0];
          return { genericErrorMessage: res.messages[0] };
        });
    };
  }

  clearErrorMessage() {
    this.errorMessage = null;
  }
}
